import type { WorkflowDraftState } from './workflow-draft-graph';

/**
 * Undo/redo history for the workflow editor's draft state, kept pure so the
 * tricky parts (what counts as an edit, what a restore carries over, how the
 * stacks bound) are unit-testable without the model, the watchables, or the DOM.
 *
 * Two stacks of whole draft-state snapshots. Snapshots are immutable values —
 * every edit replaces the draft wholesale and node drags live outside it — so
 * they are stored by reference, no cloning and no drag noise.
 *
 * Two rules encode the behaviour that bit us in testing:
 *  - {@link record} only counts an edit that changed the GRAPH. Non-graph updates
 *    still flow through the model's `updateDraftState` (changing the run
 *    idempotency key, a webhook-install patch); recording one would add an undo
 *    step that visibly does nothing.
 *  - {@link undo}/{@link redo} restore a snapshot's graph but keep the CURRENT
 *    idempotency key. The key is the workflow's run de-duplication setting, not
 *    part of the graph: it is edited in settings and saved immediately on its
 *    own. Every autosave also sends it, so restoring a snapshot's older key would
 *    silently revert that setting on the server when the undone graph saves.
 */
export class WorkflowUndoHistory {
  private past: WorkflowDraftState[] = [];
  private future: WorkflowDraftState[] = [];

  constructor(private readonly limit: number) {}

  get canUndo(): boolean {
    return this.past.length > 0;
  }

  get canRedo(): boolean {
    return this.future.length > 0;
  }

  /**
   * Record the pre-edit state when an edit changed the graph, forking the redo
   * timeline. Returns whether anything was recorded (so the caller can skip
   * refreshing derived state when nothing changed).
   */
  record(prev: WorkflowDraftState, next: WorkflowDraftState): boolean {
    if (next.graph === prev.graph) {
      return false;
    }
    this.past.push(prev);
    if (this.past.length > this.limit) {
      this.past.shift();
    }
    // A fresh edit forks the timeline: what was undone can't be redone onto it.
    this.future = [];
    return true;
  }

  /**
   * The state to apply to undo the last edit, or null when there is nothing to
   * undo. `current` is the live state, pushed onto the redo stack.
   */
  undo(current: WorkflowDraftState): WorkflowDraftState | null {
    const previous = this.past.pop();
    if (previous === undefined) {
      return null;
    }
    this.future.push(current);
    if (this.future.length > this.limit) {
      this.future.shift();
    }
    return { ...previous, idempotencyKey: current.idempotencyKey };
  }

  /** The state to apply to redo, or null when there is nothing to redo. */
  redo(current: WorkflowDraftState): WorkflowDraftState | null {
    const next = this.future.pop();
    if (next === undefined) {
      return null;
    }
    this.past.push(current);
    if (this.past.length > this.limit) {
      this.past.shift();
    }
    return { ...next, idempotencyKey: current.idempotencyKey };
  }

  /** Drop all history. Returns whether anything was dropped. */
  clear(): boolean {
    if (this.past.length === 0 && this.future.length === 0) {
      return false;
    }
    this.past = [];
    this.future = [];
    return true;
  }
}
